import { useEffect } from "react";
import { createPortal } from "react-dom";
import Button from "@/components/base/Button";
import { exportReceiptPdf } from "@/utils/receiptPdf";
import type { ReceiptData } from "@/utils/receipt";
import type { SchoolInfo } from "@/types";
import ReceiptSheet from "./ReceiptSheet";

interface Props {
  open: boolean;
  onClose: () => void;
  data: ReceiptData | null;
  school: SchoolInfo;
}

export default function ReceiptPrintModal({ open, onClose, data, school }: Props) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, onClose]);

  if (!open || !data) return null;

  return createPortal(
    <div className="print-portal fixed inset-0 z-[110] overflow-y-auto bg-foreground-950/45 backdrop-blur-[2px]">
      <div className="no-print sticky top-0 z-10 border-b border-background-200 bg-background-50/95 backdrop-blur">
        <div className="mx-auto flex w-full max-w-3xl flex-wrap items-center justify-between gap-3 px-4 py-3 md:px-8">
          <div className="flex items-center gap-3">
            <span className="w-9 h-9 rounded-md bg-primary-50 text-primary-700 flex items-center justify-center">
              <i className="ri-receipt-line text-lg"></i>
            </span>
            <div>
              <h3 className="text-sm font-semibold font-heading text-foreground-950">{data.title}</h3>
              <p className="text-xs text-foreground-500">Aperçu prêt à imprimer — {data.receiptNo}</p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="outline" icon="ri-file-pdf-2-line" onClick={() => exportReceiptPdf(data, school)}>
              Exporter en PDF
            </Button>
            <Button icon="ri-printer-line" onClick={() => window.print()}>
              Imprimer
            </Button>
            <button
              onClick={onClose}
              className="w-9 h-9 flex items-center justify-center rounded-md text-foreground-500 hover:bg-background-100 hover:text-foreground-800 transition-colors cursor-pointer"
              aria-label="Fermer"
            >
              <i className="ri-close-line text-xl"></i>
            </button>
          </div>
        </div>
      </div>

      <div className="print-pad px-4 py-6 md:px-8 md:py-10">
        <div className="mx-auto w-full max-w-[820px]">
          <ReceiptSheet data={data} school={school} />
        </div>
      </div>
    </div>,
    document.body,
  );
}